import { combineEpics } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, tap } from 'rxjs/operators';

import { Action } from '../../actions';
import { openBannersPricesPopup } from '../../actions/banners-prices';
import { openCommonPriceEditor } from '../../actions/commonPriceEditor';
import { toggleFeedFilterPriorityModal, toggleFeedFilterRateModal } from '../../actions/feed-filters/grid';
import {
    GridScreenHandleBulkItemsAction,
    openDuplicateGroupsModal,
    openMinusPhraseSelectorGridModal,
    resetGridData,
    UpdateActionProgress,
    updateActionProgress,
    updateSelection
} from '../../actions/grid-screen';
import { openPricesWizard } from '../../actions/grid-wizards';
import { openOrganizationsMassEditor } from '../../actions/organizationsEditor';
import { switchSearchAndReplaceInspectorVisibility } from '../../actions/phrases-grid';
import { phrasesSelectorGetKeywords, phrasesSelectorOpenEditor } from '../../actions/phrases-selector';
import { fetchPhrasesForPhrasesSelectorWizard, openPhrasesSelectorWizard } from '../../actions/phrasesSelectorWizard';
import { openRegionsTransformEditor } from '../../actions/regionsTransformEditor';
import { PerlRestApiResult } from '../../api/RestApiManager';
import { ofGridScreenBulkActionType } from '../../operators/massActions';
import { selectItemsActiveBrandLiftCampaigns } from '../../selectors/campaignAndGroupSelector';
import { confirm } from '../../service/Confirm/Confirm';
import { confirmBrandLiftStop } from '../../service/ConfirmBrandLiftStop/ConfirmBrandLiftStop';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import {
    showFeedFiltersDeleteSuccessMessage,
    showFeedFiltersUpdateSuccessMessage
} from '../../service/FeedFiltersHelpers/FeedFiltersHelpers';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import {
    getGridActionErrorMessage,
    getPhrasesMassActionsResultMessage,
    getTextForItemActionConfirm,
    showBannerBulkActionMessage,
    showCampaignBulkActionMessage,
    showGroupBulkActionAltMessage,
    showGroupBulkActionMessage,
    showRetargetingActionNoticeMessage
} from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { fireGridActionParams } from '../../service/Metrika/metrika';
import { getNoticeParamsForMassAction } from '../../service/NoticeTypeHelper/NoticeTypeHelper';
import { BannerBulkActionsResponse } from '../../typings/banners';
import { MassActionId } from '../../typings/bannersEditState';
import { Group, GroupBaseInfo } from '../../typings/domain';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import { RemoderateAdsCallouts } from '../../typings/graphql-grid';
import {
    BannersAction,
    CampaignsAction,
    FeedFiltersAction,
    GridAction,
    GridSelectionInfo,
    GroupsAction,
    NoticeType,
    PhrasesAction,
    RetargetingsAction,
    UsersProfileAction
} from '../../typings/grid';
import { PhrasesSelectorWizardId } from '../../typings/phrases';
import { RegionsTransformEditorId } from '../../typings/regionsTransformEditor';

const fireMetrika = (bulkAction: GridAction, selectionInfo: GridSelectionInfo): void => {
    fireGridActionParams(bulkAction, {
        campaigns: selectionInfo.campaigns.length,
        groups: selectionInfo.groups.length,
        phrases: selectionInfo.phrases.length,
        retargetings: selectionInfo.retargetings.length,
        feedFilters: selectionInfo.feedFilters.length
    });
};

const processError = (typeAction: GridAction, error: ErrorEvent): void => {
    if (error instanceof NotSyncedError || !error) {
        return;
    }

    const message =
        error instanceof ApiMethodExecutingError
            ? error.getMessage()
            : error.message || getGridActionErrorMessage(typeAction, true);

    showNoticeMessage(NoticeType.error, message);
};

type AsyncAction = (onComplete: DnaEpicAction) => DnaEpicAction;

const wrapGridBulkAction = (
    bulkAction: GridAction,
    selectionInfo: GridSelectionInfo,
    asyncAction: AsyncAction
): DnaEpicAction => {
    const onComplete: Observable<UpdateActionProgress> = of(updateActionProgress(false)).pipe(
        tap((): void => fireMetrika(bulkAction, selectionInfo))
    );

    return concat(
        of(updateActionProgress(true)),
        from(asyncAction(onComplete)).pipe(
            catchError(
                (err: ErrorEvent): DnaEpicAction => {
                    processError(bulkAction, err);
                    return of(updateActionProgress(false));
                }
            )
        )
    );
};

const campaignBulkActionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(
            CampaignsAction.archive,
            CampaignsAction.unarchive,
            CampaignsAction.resume,
            CampaignsAction.remoderate,
            CampaignsAction.copy,
            CampaignsAction.addToFavorites,
            CampaignsAction.removeFromFavorites
        ),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { campaigns } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.restApiManager.executeCampaignsGridItemsAction(bulkAction, campaigns, login)).pipe(
                            mergeMap(
                                (response: PerlRestApiResult): DnaEpicAction => {
                                    const params = getNoticeParamsForMassAction(response, campaigns.length);

                                    showCampaignBulkActionMessage(bulkAction, params);

                                    return concat(onComplete, of(resetGridData()));
                                }
                            )
                        )
                );
            }
        )
    );

const campaignBulkStopEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(CampaignsAction.stop),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { campaigns } = selectionInfo;
                let confirmPromise = Promise.resolve();

                const itemsActiveBrandLift = selectItemsActiveBrandLiftCampaigns(state, campaigns);

                if (itemsActiveBrandLift.length > 0) {
                    const items = itemsActiveBrandLift.map(item => ({
                        name: item.name,
                        id: item.id
                    }));

                    confirmPromise = confirmBrandLiftStop(items);
                }

                return from(confirmPromise).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(
                                    deps.restApiManager.executeCampaignsGridItemsAction(bulkAction, campaigns, login)
                                ).pipe(
                                    mergeMap(
                                        (response: PerlRestApiResult): DnaEpicAction => {
                                            const params = getNoticeParamsForMassAction(response, campaigns.length);

                                            showCampaignBulkActionMessage(bulkAction, params);

                                            return concat(onComplete, of(resetGridData()));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const campaignBulkDeleteEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(CampaignsAction.delete),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { campaigns } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(
                                    deps.restApiManager.executeCampaignsGridItemsAction(bulkAction, campaigns, login)
                                ).pipe(
                                    mergeMap(
                                        (response: PerlRestApiResult): DnaEpicAction => {
                                            const params = getNoticeParamsForMassAction(response, campaigns.length);

                                            showCampaignBulkActionMessage(bulkAction, params);

                                            return concat(onComplete, of(resetGridData()), of(updateSelection([])));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const groupBulkBasicActionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(
            GroupsAction.resume,
            GroupsAction.stop,
            GroupsAction.archive,
            GroupsAction.unarchive
        ),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: {
                        selectionInfo,
                        gridData: { data }
                    }
                } = state;
                const { groups } = selectionInfo;

                const campaignId = GroupsHelpers.getFirstCampaignIdForGroups(
                    selectionInfo.groups,
                    data as GroupBaseInfo[]
                );

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(
                            deps.restApiManager.executeGroupsGridItemsAction(bulkAction, groups, login, campaignId)
                        ).pipe(
                            mergeMap(
                                (response: PerlRestApiResult): DnaEpicAction => {
                                    const params = getNoticeParamsForMassAction(response);

                                    showGroupBulkActionMessage(bulkAction, params);

                                    return concat(onComplete, of(resetGridData()));
                                }
                            )
                        )
                );
            }
        )
    );

const groupBulkAdvancedActionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(
            GroupsAction.sendModerateByAgency,
            GroupsAction.sendModerateByClient,
            GroupsAction.admSendMD
        ),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { groups } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.restApiManager.executeDnaGroupsGridItemsAction(bulkAction, groups, login)).pipe(
                            mergeMap(
                                (response: PerlRestApiResult): DnaEpicAction => {
                                    const params = getNoticeParamsForMassAction(response);

                                    showGroupBulkActionMessage(bulkAction, params);

                                    return concat(onComplete, of(resetGridData()));
                                }
                            )
                        )
                );
            }
        )
    );

const groupBulkMoreAdvancedActionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.moderateAccept, GroupsAction.delete, GroupsAction.sendBK),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { groups } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(
                                    deps.restApiManager.executeDnaGroupsGridItemsAction(bulkAction, groups, login)
                                ).pipe(
                                    mergeMap(
                                        (response: PerlRestApiResult): DnaEpicAction => {
                                            const params = getNoticeParamsForMassAction(response);

                                            showGroupBulkActionMessage(bulkAction, params);

                                            return concat(onComplete, of(updateSelection([])), of(resetGridData()));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const groupBulkRemoderateBannersAdditionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.remoderateBannersAdditions),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { groups } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.groupsMutationExecutor.sendRemoderateAdsCallouts({ adGroupIds: groups }, login)).pipe(
                            mergeMap(
                                (response: RemoderateAdsCallouts): DnaEpicAction => {
                                    const { processedAdGroupIds, skippedAdGroupIds } = response.remoderateAdsCallouts;

                                    showGroupBulkActionAltMessage(
                                        bulkAction,
                                        groups,
                                        processedAdGroupIds.length,
                                        skippedAdGroupIds.length
                                    );

                                    return concat(onComplete, of(resetGridData()));
                                }
                            )
                        )
                );
            }
        )
    );

const groupBulkModerateAcceptBannersAdditionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.moderateAcceptBannersAdditions),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { groups } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(
                                    deps.groupsMutationExecutor.sendRemoderateAcceptAdsCallouts(
                                        { adGroupIds: groups },
                                        login
                                    )
                                ).pipe(
                                    mergeMap(
                                        (response: RemoderateAdsCallouts): DnaEpicAction => {
                                            const {
                                                processedAdGroupIds,
                                                skippedAdGroupIds
                                            } = response.remoderateAdsCallouts;

                                            showGroupBulkActionAltMessage(
                                                bulkAction,
                                                groups,
                                                processedAdGroupIds.length,
                                                skippedAdGroupIds.length
                                            );

                                            return concat(onComplete, of(resetGridData()));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const groupBulkOpenMinusPhraseSelectorEpic: DnaEpic = (action$, store$) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.openMinusPhraseSelector),
        map(() => openMinusPhraseSelectorGridModal(store$.value.grid.selectionInfo.groups))
    );

const groupBulkOpenPhrasesSelectorWizardEpic: DnaEpic = (action$, store$) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.openPhrasesSelectorWizard),
        filter(() => !store$.value.commonData.operatorFeatures.isOldPhraseSelectorWizardDisabled),
        mergeMap(() => {
            const {
                grid: {
                    selectionInfo: {
                        groups: [groupId]
                    },
                    gridData
                }
            } = store$.value;
            const gridGroup = gridData.data.find(el => el && el.id === groupId) as Group;
            const group = { ...gridGroup, groupType: gridGroup.type };

            return [
                openPhrasesSelectorWizard(PhrasesSelectorWizardId.groupsGrid, group),
                fetchPhrasesForPhrasesSelectorWizard(PhrasesSelectorWizardId.groupsGrid, group)
            ];
        })
    );

const groupBulkOpenRegionsTransformEditorEpic: DnaEpic = (action$, store$) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.regionsTransform),
        map(() => {
            const state = store$.value;
            const {
                grid: {
                    selectionInfo: { campaigns, groups }
                }
            } = state;

            return openRegionsTransformEditor(RegionsTransformEditorId.groupsGrid, campaigns, groups);
        })
    );

const groupBulkOpenPhrasesSelectorEpic: DnaEpic = (action$, store$) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.openPhrasesSelector, GroupsAction.openPhrasesSelectorWizard),
        filter(
            ({ bulkAction }: GridScreenHandleBulkItemsAction) =>
                bulkAction === GroupsAction.openPhrasesSelector ||
                store$.value.commonData.operatorFeatures.isOldPhraseSelectorWizardDisabled
        ),
        mergeMap(() => {
            const {
                grid: {
                    selectionInfo: {
                        groups: [groupId]
                    },
                    gridData
                }
            } = store$.value;
            const gridGroup = gridData.data.find(el => el && el.id === groupId) as Group;
            const group = { ...gridGroup, groupType: gridGroup.type, keywords: {} };

            return [
                phrasesSelectorOpenEditor(PhrasesSelectorWizardId.groupsGrid, group),
                phrasesSelectorGetKeywords(PhrasesSelectorWizardId.groupsGrid, gridGroup.campaign.id, gridGroup.id)
            ];
        })
    );

const groupBulkCopyEpic: DnaEpic = (action$, store$) =>
    action$.pipe(
        ofGridScreenBulkActionType(GroupsAction.copy),
        map(() => {
            const {
                commonData: { login },
                grid: {
                    selectionInfo,
                    gridData: { cacheKey }
                }
            } = store$.value;

            return openDuplicateGroupsModal(selectionInfo.campaigns, selectionInfo.groups, cacheKey || '', login);
        })
    );

const bannerBulkActionsEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(
            BannersAction.stop,
            BannersAction.resume,
            BannersAction.archive,
            BannersAction.unarchive
        ),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { banners } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(
                            deps.bannersMutationExecutor.runMassAction(
                                { adIds: banners },
                                login,
                                bulkAction as BannersAction
                            )
                        ).pipe(
                            mergeMap(
                                (response: BannerBulkActionsResponse): DnaEpicAction => {
                                    showBannerBulkActionMessage(bulkAction, response);

                                    return concat(onComplete, of(resetGridData()));
                                }
                            )
                        )
                );
            }
        )
    );

const bannerBulkDeleteEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(BannersAction.delete),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { banners } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(
                                    deps.bannersMutationExecutor.runMassAction(
                                        { adIds: banners },
                                        login,
                                        bulkAction as BannersAction
                                    )
                                ).pipe(
                                    mergeMap(
                                        (response: BannerBulkActionsResponse): DnaEpicAction => {
                                            showBannerBulkActionMessage(bulkAction, response);

                                            return concat(onComplete, of(updateSelection([])), of(resetGridData()));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const bannerBulkEditPricesEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(BannersAction.editPrices),
        mapTo(openBannersPricesPopup(MassActionId.bannerGrid))
    );

const bannerBulkEditOrganizationsEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(BannersAction.editYellowPagesData),
        mapTo(openOrganizationsMassEditor(MassActionId.bannerGrid))
    );

const phrasesBulkDeleteEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(PhrasesAction.delete),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { phrases } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(() =>
                        wrapGridBulkAction(
                            bulkAction,
                            selectionInfo,
                            (onComplete: Observable<Action>): DnaEpicAction =>
                                from(deps.phrasesMutationExecutor.deleteKeywords({ keywordIds: phrases }, login)).pipe(
                                    mergeMap(
                                        (): DnaEpicAction => {
                                            showNoticeMessage(
                                                NoticeType.success,
                                                getPhrasesMassActionsResultMessage(bulkAction)
                                            );

                                            return concat(onComplete, of(updateSelection([])), of(resetGridData()));
                                        }
                                    )
                                )
                        )
                    ),
                    catchError(() => EMPTY)
                );
            }
        )
    );

const phrasesBulkEnableEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(PhrasesAction.enable),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { phrases } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.phrasesMutationExecutor.unsuspendKeywords({ keywordIds: phrases }, login)).pipe(
                            mergeMap(() => {
                                showNoticeMessage(NoticeType.success, getPhrasesMassActionsResultMessage(bulkAction));

                                return concat(onComplete, of(resetGridData()));
                            })
                        )
                );
            }
        )
    );

const phrasesBulkDisableEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(PhrasesAction.disable),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { phrases } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.phrasesMutationExecutor.suspendKeywords({ keywordIds: phrases }, login)).pipe(
                            mergeMap(() => {
                                showNoticeMessage(NoticeType.success, getPhrasesMassActionsResultMessage(bulkAction));

                                return concat(onComplete, of(resetGridData()));
                            })
                        )
                );
            }
        )
    );

const phrasesBulkPricesWizardEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(PhrasesAction.pricesWizard),
        mapTo(openPricesWizard())
    );

const phrasesBulkSearchAndReplaceEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(PhrasesAction.openSearchAndReplaceInspector),
        mapTo(switchSearchAndReplaceInspectorVisibility(true))
    );

const retargetingsBulkResumeEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(RetargetingsAction.resume),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { retargetings } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.retargetingMutationExecutor.resumeRetargetings(retargetings, login)).pipe(
                            mergeMap(({ resumeRetargetings: { successCount, totalCount } }) => {
                                showRetargetingActionNoticeMessage(bulkAction, successCount, totalCount);

                                return concat(onComplete, of(resetGridData()));
                            })
                        )
                );
            }
        )
    );

const retargetingsBulkSuspendEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(RetargetingsAction.suspend),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { retargetings } = selectionInfo;

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: Observable<Action>): DnaEpicAction =>
                        from(deps.retargetingMutationExecutor.suspendRetargetings(retargetings, login)).pipe(
                            mergeMap(({ suspendRetargetings: { successCount, totalCount } }) => {
                                showRetargetingActionNoticeMessage(bulkAction, successCount, totalCount);

                                return concat(onComplete, of(resetGridData()));
                            })
                        )
                );
            }
        )
    );

const retargetingsBulkDeleteEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(RetargetingsAction.delete),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const { retargetings } = selectionInfo;

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(
                        (): DnaEpicAction =>
                            wrapGridBulkAction(
                                bulkAction,
                                selectionInfo,
                                (onComplete: Observable<Action>): DnaEpicAction =>
                                    from(deps.retargetingMutationExecutor.deleteRetargetings(retargetings, login)).pipe(
                                        mergeMap(
                                            ({ deleteRetargetings: { successCount, totalCount } }): DnaEpicAction => {
                                                showRetargetingActionNoticeMessage(
                                                    bulkAction,
                                                    successCount,
                                                    totalCount
                                                );

                                                return concat(onComplete, of(resetGridData()), of(updateSelection([])));
                                            }
                                        )
                                    )
                            )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const feedFiltersBulkChangeRateEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(FeedFiltersAction.changeRate),
        mapTo(toggleFeedFilterRateModal())
    );

const feedFiltersBulkChangeAutobudgetEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(FeedFiltersAction.changeAutobudget),
        mapTo(toggleFeedFilterPriorityModal())
    );

const feedFiltersBulkDeleteEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(FeedFiltersAction.delete),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const deleteItems = selectionInfo.feedFilters.map(id => ({ id }));

                return from(confirm({ message: getTextForItemActionConfirm(bulkAction) })).pipe(
                    mergeMap(
                        (): DnaEpicAction =>
                            wrapGridBulkAction(
                                bulkAction,
                                selectionInfo,
                                (onComplete: DnaEpicAction): DnaEpicAction =>
                                    from(
                                        deps.smartFiltersMutationExecutor.deleteSmartFilters({ deleteItems }, login)
                                    ).pipe(
                                        mergeMap(
                                            ({ data }): DnaEpicAction => {
                                                if (data) {
                                                    showFeedFiltersDeleteSuccessMessage(data, bulkAction);
                                                }

                                                return concat(onComplete, of(updateSelection([])), of(resetGridData()));
                                            }
                                        )
                                    )
                            )
                    ),
                    catchError((): DnaEpicAction => EMPTY)
                );
            }
        )
    );

const feedFiltersBulkToggleEpic: DnaEpic = (action$, store$, deps) =>
    action$.pipe(
        ofGridScreenBulkActionType(FeedFiltersAction.disable, FeedFiltersAction.enable),
        mergeMap(
            ({ bulkAction }: GridScreenHandleBulkItemsAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: { selectionInfo }
                } = state;
                const updateItems = selectionInfo.feedFilters.map(id => ({
                    id,
                    isSuspended: bulkAction === FeedFiltersAction.disable
                }));

                return wrapGridBulkAction(
                    bulkAction,
                    selectionInfo,
                    (onComplete: DnaEpicAction): DnaEpicAction =>
                        from(deps.smartFiltersMutationExecutor.updateSmartFilters({ updateItems }, login)).pipe(
                            mergeMap(({ data }) => {
                                if (data) {
                                    showFeedFiltersUpdateSuccessMessage(data, bulkAction);
                                }

                                return concat(onComplete, of(resetGridData()));
                            })
                        )
                );
            }
        )
    );

const showConditionOpenPriceEditorEpic: DnaEpic = action$ =>
    action$.pipe(
        ofGridScreenBulkActionType(
            PhrasesAction.setPhrasesCommonPriceEditor,
            RetargetingsAction.setRetargetingCommonPriceEditor,
            UsersProfileAction.setUsersProfileCommonPriceEditor
        ),
        mapTo(openCommonPriceEditor())
    );

export const gridScreenMassActionsEpic = combineEpics(
    campaignBulkActionsEpic,
    campaignBulkStopEpic,
    campaignBulkDeleteEpic,
    groupBulkBasicActionsEpic,
    groupBulkAdvancedActionsEpic,
    groupBulkMoreAdvancedActionsEpic,
    groupBulkRemoderateBannersAdditionsEpic,
    groupBulkModerateAcceptBannersAdditionsEpic,
    groupBulkOpenMinusPhraseSelectorEpic,
    groupBulkOpenPhrasesSelectorWizardEpic,
    groupBulkOpenPhrasesSelectorEpic,
    groupBulkOpenRegionsTransformEditorEpic,
    groupBulkCopyEpic,
    bannerBulkActionsEpic,
    bannerBulkDeleteEpic,
    bannerBulkEditPricesEpic,
    bannerBulkEditOrganizationsEpic,
    phrasesBulkDeleteEpic,
    phrasesBulkEnableEpic,
    phrasesBulkDisableEpic,
    phrasesBulkPricesWizardEpic,
    phrasesBulkSearchAndReplaceEpic,
    retargetingsBulkResumeEpic,
    retargetingsBulkSuspendEpic,
    retargetingsBulkDeleteEpic,
    feedFiltersBulkChangeRateEpic,
    feedFiltersBulkChangeAutobudgetEpic,
    feedFiltersBulkToggleEpic,
    feedFiltersBulkDeleteEpic,
    showConditionOpenPriceEditorEpic
);
